/**
 * Notifications Context (Phase 3 Sprint C)
 *
 * Loads the user's notifications on protected routes, subscribes to the SSE
 * stream, and exposes list/unread/markRead/markAllRead/loadMore. Also renders a
 * lightweight in-house toast stack (no extra dependency) for new live events.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, fetchWithCsrf } from '../config/api';
import { useNotificationStream } from '../hooks/useNotificationStream';
import { routeForNotification } from '../utils/notificationRoute';

const NotificationsContext = createContext(null);
const PROTECTED_PREFIXES = ['/dashboard'];

export function NotificationsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const enabled = PROTECTED_PREFIXES.some((p) => location.pathname.startsWith(p));

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);
  // New (<= 30 days) fraud alerts and hot scams this user has not opened -
  // the sidebar badge (10-06). Same count the desktop ribbon shows.
  const [newAlerts, setNewAlerts] = useState({ count: 0, bySource: {} });

  const loadNewAlerts = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bff/alerts/new-count`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setNewAlerts({ count: Number(d.count) || 0, bySource: d.bySource || {} });
      }
    } catch (e) { /* badge just keeps its last value */ }
  }, [enabled]);

  useEffect(() => { loadNewAlerts(); }, [loadNewAlerts]);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/bff/notifications?limit=30`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
        setUnread(d.unread || 0);
        setNextBefore(d.nextBefore || null);
      }
    } catch (e) {
      // ignore — bell just stays empty
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const dismissToast = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const addToast = useCallback((n) => {
    const id = (toastId.current += 1);
    setToasts((t) => [...t, { id, title: n.title, body: n.body, type: n.type, payload: n.payload }]);
    setTimeout(() => dismissToast(id), 5000);
  }, [dismissToast]);

  useNotificationStream({
    enabled,
    onNotification: (n) => {
      setItems((prev) => [n, ...prev]);
      setUnread((u) => u + 1);
      addToast(n);
      if (n && ['ic3_alert', 'psa_alert', 'hot_scam'].includes(n.type)) loadNewAlerts();
    },
    onMeta: (m) => {
      if (m && typeof m.unread === 'number') setUnread(m.unread);
    },
    // Published / removed / opened: re-count rather than guess.
    onAlertsChanged: () => loadNewAlerts(),
  });

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (
      n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n
    )));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await fetchWithCsrf(`${API_BASE_URL}/bff/notifications/${id}/read`, { method: 'PATCH' });
    } catch (e) { /* optimistic; ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setUnread(0);
    try {
      await fetchWithCsrf(`${API_BASE_URL}/bff/notifications/mark-all-read`, { method: 'POST' });
    } catch (e) { /* optimistic; ignore */ }
  }, []);

  const remove = useCallback(async (id) => {
    const target = items.find((n) => n.id === id);
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (target && !target.read_at) setUnread((u) => Math.max(0, u - 1));
    try {
      await fetchWithCsrf(`${API_BASE_URL}/bff/notifications/${id}`, { method: 'DELETE' });
    } catch (e) { /* optimistic; ignore */ }
  }, [items]);

  const loadMore = useCallback(async () => {
    if (!nextBefore) return;
    try {
      const r = await fetch(
        `${API_BASE_URL}/bff/notifications?limit=30&before=${nextBefore}`,
        { credentials: 'include' }
      );
      if (r.ok) {
        const d = await r.json();
        setItems((prev) => [...prev, ...(d.items || [])]);
        setNextBefore(d.nextBefore || null);
      }
    } catch (e) { /* ignore */ }
  }, [nextBefore]);

  const value = {
    items, unread, loading, nextBefore, markRead, markAllRead, remove, loadMore, refetch: load,
    newAlerts, refreshNewAlerts: loadNewAlerts,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 w-80 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            onClick={() => {
              const dest = routeForNotification(t);
              dismissToast(t.id);
              if (/^https?:\/\//i.test(dest)) window.open(dest, '_blank', 'noopener,noreferrer');
              else navigate(dest);
            }}
            className="pointer-events-auto bg-white border border-gray-200 shadow-lg rounded-xl p-3 cursor-pointer"
          >
            <p className="text-sm font-semibold text-black">{t.title}</p>
            {t.body && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{t.body}</p>}
          </div>
        ))}
      </div>
    </NotificationsContext.Provider>
  );
}

const EMPTY = {
  items: [], unread: 0, loading: false, nextBefore: null,
  markRead: () => {}, markAllRead: () => {}, remove: () => {}, loadMore: () => {}, refetch: () => {},
  newAlerts: { count: 0, bySource: {} },
  refreshNewAlerts: () => {},
};

export function useNotifications() {
  return useContext(NotificationsContext) || EMPTY;
}
